import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';
import { InventoryFields } from 'common/modules/inventory';
import { api } from 'common/frontend/api';
import * as ShopSelectors from 'selectors/ShopSelectors';
import ActionDialog from 'common/components/ActionDialog';
import { useTranslation } from 'services/localization/useTranslation';
import { FieldValues, BatchManager } from 'common/frontend/firebase/firestore';

interface Props {
	isOpen: boolean;
	columnId: string;
	inventoryFields: InventoryFields;
	onClose: () => void;
}

const DeleteColumnDialog = (props: Props) => {
	const { t } = useTranslation();
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(false);
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const columnLabel = props.inventoryFields.values[props.columnId]?.label ?? '';

	const handleDelete = async () => {
		setSaving(true);
		try {
			await api()
				.inventoryConfigurations.doc(shopId)
				.update({
					'fields.order': FieldValues.arrayRemove(props.columnId),
					[`fields.values.${props.columnId}` as `fields.values.${123}`]: FieldValues.delete(),
				});

			const inventoryItemsToUpdate = await api()
				.inventoryItems.get.orderBy(`fieldValues.${props.columnId}` as `fieldValues.${123}`)
				.startAfter(null)
				.get();

			const batchManager = new BatchManager();
			inventoryItemsToUpdate.forEach((item) => {
				api(batchManager.batch())
					.inventoryItems.doc(item.id)
					.update({
						[`fieldValues.${props.columnId}` as `fieldValues.${123}`]: FieldValues.delete() as any,
					});
			});

			await batchManager.commit();

			setSaving(false);
			props.onClose();
		} catch (e) {
			setSaving(false);
			setError(true);
		}
		setSaving(false);
	};

	return props.isOpen ? (
		<ActionDialog
			title={t('inventory.articles.table.columnsDeleteTitle', 'Delete column?')}
			headerVariant="contained"
			btnActions={{
				primary: {
					text: t('common:actions.delete'),
					loading: saving,
					disabled: saving,
					onClick: handleDelete,
				},
				secondary: {
					text: t('common:actions.cancel'),
					disabled: saving,
					onClick: props.onClose,
				},
			}}
			onClose={props.onClose}
			variant="danger"
		>
			<Box mt={1}>
				<Typography variant="body1">
					{t('inventory.articles.table.columnsDeleteBody1', {
						columnLabel,
						defaultValue:
							'By deleting column "{{columnLabel}}", all specifications created for the column will also be deleted.',
					})}
				</Typography>
			</Box>
			<Box mt={1}>
				<Typography variant="body1">
					{t('inventory.articles.table.columnsDeleteBody2', 'This action cannot be undone.')}
				</Typography>
			</Box>
			<Box mt={1}>
				<Typography variant="body1" color="textSecondary">
					{t(
						'inventory.articles.table.columnsDeleteBody3',
						'Tip: You can also hide a column from the Column filter',
					)}
				</Typography>
			</Box>
			{error && (
				<Box mt={1}>
					<Typography variant="body2" color="error">
						{t('common:actions.pleaseTryAgain')}
					</Typography>
				</Box>
			)}
		</ActionDialog>
	) : null;
};

export default DeleteColumnDialog;
